import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import Select from "react-select";
import {
    languageChange,
    currentSelectedLanguage,
} from "../../../../StateManagement/actions";
import { useSelector, useDispatch } from "react-redux";
import FeatureTooltip from "./ToolTipDisplay"
import { Send, Share } from "lucide-react";
import { Template } from "ckeditor5";
import APIRequest from "../../../../Services/commonAPIConsumer";
import Alert from "../../../Controls/Alerts/Alerts";
import LoadingSpinner from "../../../Controls/Spinner/LoadingSpinner";
import { setDocumentTemplates, storeTemplateIdentifyLeakers } from "../../../../StateManagement/actions";
import IdentifyLeakersModal from "../IdentifyLeakers/IdentifyLeakers";
import { useFetchIdentifyLeakers } from "../IdentifyLeakers/useIdentifyLeakers";

const DualListSelector = forwardRef(({
    availableItems = [],
    securityLevel,
    template,
    onChange,
    LEVEL1_ALLOWED = [],
    LEVEL2_ALLOWED = [],
    TEMPLATE_FEATURE_MAP,
    isEdit = false,
    isOpen,
    isSecurityLevelChanged,
    accountMaxValues = {},
    onMaxValueChange,
    maxReadsText,
    maxViewsPerDocumentText,
    maxPrintsPerReaderText,
    maxPrintsPerDocumentText,
    onAlert,
    onRestrictionValueChange,
    restrictToDomainLabel = "Restrict to domains",
    allowedIPLabel = "Allowed IP",
    bannedRegionsLabel = "Banned regions",
    ConstContinents,
    ConstMultipleIps,
    Domains
}, ref) => {
    const [leftList, setLeftList] = useState(availableItems);
    const [rightList, setRightList] = useState([]);
    const [selectedLeft, setSelectedLeft] = useState([]);
    const [selectedRight, setSelectedRight] = useState([]);
    const selectedLanguage = useSelector(currentSelectedLanguage);

    const RegionValues = [
        { value: 0, label: selectedLanguage.LstLanguageCodes.UI_Europe },
        { value: 2, label: selectedLanguage.LstLanguageCodes.UI_NorthAmerica },
        { value: 3, label: selectedLanguage.LstLanguageCodes.UI_SouthAmerica },
        { value: 4, label: selectedLanguage.LstLanguageCodes.UI_Asia },
        { value: 5, label: selectedLanguage.LstLanguageCodes.UI_Oceania },
        { value: 1, label: selectedLanguage.LstLanguageCodes.UI_Africa },
    ];

    const MAX_VALUE_KEYS = [
        selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader,
        selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument,
    ];
    const L2_L3_ONLY_MAX = [selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader];
    const CAN_PRINT_LABEL = selectedLanguage.LstLanguageCodes.UI_CanPrint;
    const DYNAMIC_RESTRICTION_KEYS = [restrictToDomainLabel, allowedIPLabel, bannedRegionsLabel];
    const DYNAMIC_L2L3_ONLY_RESTRICTIONS = [restrictToDomainLabel];
    // const BANNED_REGIONS_LABEL = bannedRegionsLabel;

    const prevSecurityLevelRef = useRef(securityLevel);
    const initialIdLeakerData = useSelector((state) => state.identifyLeakers);
    const dispatch = useDispatch();

    const [isSaved, setIsSaved] = useState(false);
    const { getContentSecuritySettings, fetchLookupData } = useFetchIdentifyLeakers();

    const [localMaxValues, setLocalMaxValues] = useState(() => ({
        [selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader]: maxReadsText || 0,
        [selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument]: maxViewsPerDocumentText || 0,
    }));

    const [printPopup, setPrintPopup] = useState({
        visible: false,
        perReaderVal: "",
        perDocumentVal: "",
    });
    const userRemovedMaxItems = useRef(new Set());
    const textBoxRef = useRef(null);
    const restrictionInputRef = useRef(null);
    const editInputRef = useRef(null);
    const hasInitialized = useRef(false);

    const [localRestrictionValues, setLocalRestrictionValues] = useState({
        [restrictToDomainLabel]: "",
        [allowedIPLabel]: "",
        [bannedRegionsLabel]: [],
    });
    const userRemovedRestrictionItems = useRef(new Set());
    const userEditedRestrictionItems = useRef(new Set());
    const pendingRestrictionBackup = useRef(null);
    const initialDomainsRef = useRef(null);
    const pendingPrintBackup = useRef(null);
    const [restrictionPopup, setRestrictionPopup] = useState({ visible: false, key: null, pendingMoveItem: null });
    const [editPopup, setEditPopup] = useState({ visible: false, key: null, inputVal: "" });
    const [dualAlert, setDualAlert] = useState({ msg: "", type: "" });
    const identifyLeakersModalRef = useRef(null);
    const userEditedPrintValues = useRef({ perReaderVal: false, perDocumentVal: false });
    const documentIdLeakers = useSelector((state) => state.identifyLeakers);
    const templateIdLeakers = useSelector((state) => state.templateIdentifyLeakers);
    const handleEditClick = async () => {
        if (template && isEdit) {
            dispatch(storeTemplateIdentifyLeakers({
                cipherText: template.cipherText || [],
                floatingText: template.floatingText || []
            }));
        }
        if (identifyLeakersModalRef.current) {
            identifyLeakersModalRef.current.showModal();
        }
        await fetchLookupData();
    };
    const handleClose = (_, idLeakerData) => {
        if (identifyLeakersModalRef.current) {
            identifyLeakersModalRef.current.closeModal();
        }
    };
    useImperativeHandle(ref, () => ({
        validate() {
            const selectedLabels = rightList.map(item => item.label || item);
            for (const label of selectedLabels) {
                if (MAX_VALUE_KEYS.includes(label)) {
                    const val = localMaxValues[label];
                    if (!(parseInt(val) > 0)) {
                        const msgKey =
                            label === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader
                                ? selectedLanguage.LstLanguageCodes.UI_MaxReadsperReaderCannotBeEmpty
                                : selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocumentCannotBeEmpty;
                        onAlert?.(msgKey);
                        return false;
                    }
                }
                if (DYNAMIC_RESTRICTION_KEYS.includes(label)) {
                    const val = localRestrictionValues[label];
                    const isEmpty = Array.isArray(val) ? val.length === 0 : !val || val.trim() === "";
                    if (isEmpty) {
                        const messages = {
                            [restrictToDomainLabel]: selectedLanguage.LstLanguageCodes.UI_Atleastonedomainisrequiredtoselectthisfeature,
                            [allowedIPLabel]: selectedLanguage.LstLanguageCodes.UI_AtleastoneIPisrequiredtoselectthisfeature,
                            [bannedRegionsLabel]: selectedLanguage.LstLanguageCodes.UI_AtleastoneBannedRegionisrequiredtoselectthisfeature,
                        };
                        onAlert?.(messages[label]);
                        return false;
                    }
                }
            }
            return true;
        }
    }));
    useEffect(() => {
        if (!isOpen) {
            hasInitialized.current = false;
            return;
        }
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        userRemovedMaxItems.current = new Set();
        userRemovedRestrictionItems.current = new Set();
        // userEditedRestrictionItems.current = new Set();
        const newRestrictionValues = {
            [restrictToDomainLabel]: Domains || "",
            [allowedIPLabel]: Array.isArray(ConstMultipleIps)
                ? ConstMultipleIps.filter(Boolean).join(",")
                : (ConstMultipleIps || ""),
            [bannedRegionsLabel]: Array.isArray(ConstContinents)
                ? ConstContinents.map(Number).filter(n => !isNaN(n))
                : (typeof ConstContinents === "string" && ConstContinents.trim()
                    ? ConstContinents.split(",").map(s => Number(s.trim())).filter(n => !isNaN(n))
                    : []),
        };
        setLocalRestrictionValues(prev => {
            const merged = { ...newRestrictionValues };
            for (const key of userEditedRestrictionItems.current) {
                merged[key] = prev[key];
            }
            return merged;
        });
        const initPerReader = userEditedPrintValues.current.perReaderVal
            ? printPopup.perReaderVal
            : (printPopup.perReaderVal ||
                (isEdit && template
                    ? (template.maxPrintsPerReader > 0 ? String(template.maxPrintsPerReader) : "")
                    : (maxPrintsPerReaderText > 0 ? String(maxPrintsPerReaderText) : "")));
        const initPerDocument = userEditedPrintValues.current.perDocumentVal
            ? printPopup.perDocumentVal
            : (printPopup.perDocumentVal ||
                (isEdit && template
                    ? (template.maxPrintsPerDocument > 0 ? String(template.maxPrintsPerDocument) : "")
                    : (maxPrintsPerDocumentText > 0 ? String(maxPrintsPerDocumentText) : "")));
        onMaxValueChange?.({
            maxReadsPerReader: localMaxValues[selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader] || 0,
            maxViewsPerDocument: localMaxValues[selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument] || 0,
            maxPrintsPerReader: parseInt(initPerReader, 10) || 0,
            maxPrintsPerDocument: parseInt(initPerDocument, 10) || 0,
        });
        onRestrictionValueChange?.({
            restrictToDomains: newRestrictionValues[restrictToDomainLabel],
            allowedIP: newRestrictionValues[allowedIPLabel],
            bannedRegions: newRestrictionValues[bannedRegionsLabel],
        });
    }, [isOpen, Domains, ConstMultipleIps, ConstContinents]);

    useEffect(() => {
        setLocalMaxValues(prev => ({
            ...prev,
            [selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader]: maxReadsText || 0,
            [selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument]: maxViewsPerDocumentText || 0,
        }));
    }, [maxReadsText, maxViewsPerDocumentText]);

    useEffect(() => {
        if (!dualAlert.msg) return;
        const t = setTimeout(() => setDualAlert({ msg: "", type: "" }), 4000);
        return () => clearTimeout(t);
    }, [dualAlert.msg]);

    useEffect(() => {
        if (!securityLevel) return;
        if (prevSecurityLevelRef.current === securityLevel) return;

        let allowed = [];
        if (isEdit === true && !isSecurityLevelChanged) {
            allowed = template?.rDocsLevel === 1 ? LEVEL1_ALLOWED : LEVEL2_ALLOWED;
        } else {
            allowed = securityLevel === 1 ? LEVEL1_ALLOWED : LEVEL2_ALLOWED;
        }
        if (securityLevel === 1) {
            const readsKey = selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader;
            const resetMaxValues = { ...localMaxValues, [readsKey]: maxReadsText || 0 };
            setLocalMaxValues(resetMaxValues);
            setPrintPopup(prev => ({ ...prev, perReaderVal: "" }));
            notifyMaxValueChange(resetMaxValues, "", printPopup.perDocumentVal);
            setLocalRestrictionValues(prev => ({ ...prev, [restrictToDomainLabel]: "" }));
        }
        else if (prevSecurityLevelRef.current == 1) {
            const restoredPerReader = isEdit && template
                ? (template.maxPrintsPerReader > 0 ? String(template.maxPrintsPerReader) : "")
                : (maxPrintsPerReaderText > 0 ? String(maxPrintsPerReaderText) : "");
            // Always restore from the frozen initial value so repeated 1→2
            // toggles never pull a stale "" that was set during level-1 phase.
            const restoreddomain = initialDomainsRef.current !== null
                ? initialDomainsRef.current
                : (isEdit && template
                    ? (template.domains || template.restricttoDomains || template.restrictToDomains) || ""
                    : Domains || "");


            setPrintPopup(prev => ({
                ...prev,
                perReaderVal: restoredPerReader
            }));
            setLocalRestrictionValues(prev => ({ ...prev, [restrictToDomainLabel]: restoreddomain }));
            notifyMaxValueChange(localMaxValues, restoredPerReader, printPopup.perDocumentVal);
        }
        setRightList(prevSelected => {
            const stillAllowed = prevSelected.filter(item => {
                const label = item.label || item;
                if (L2_L3_ONLY_MAX.includes(label) && securityLevel === 1) return false;
                if (DYNAMIC_L2L3_ONLY_RESTRICTIONS.includes(label) && securityLevel === 1) return false;
                return allowed.includes(label) || MAX_VALUE_KEYS.includes(label) || DYNAMIC_RESTRICTION_KEYS.includes(label);
            });
            const movedBack = prevSelected.filter(item => !stillAllowed.includes(item));
            setLeftList(prevAvailable => {
                const merged = [...prevAvailable, ...movedBack];
                return merged.filter(
                    (item, index, self) =>
                        index ===
                        self.findIndex(
                            i => (i.label || i) === (item.label || item)
                        )
                );
            });
            setSelectedRight(prev => prev.filter(item => stillAllowed.includes(item)));
            setSelectedLeft(prev => prev.filter(item => {
                const label = item.label || item;
                const isMaxItem = MAX_VALUE_KEYS.includes(label);
                const isRestrictionRow = DYNAMIC_RESTRICTION_KEYS.includes(label);
                if (L2_L3_ONLY_MAX.includes(label) && securityLevel === 1) return false;
                if (DYNAMIC_L2L3_ONLY_RESTRICTIONS.includes(label) && securityLevel === 1) return false;
                if (securityLevel === 1 && !LEVEL1_ALLOWED.includes(label) && !isMaxItem && !isRestrictionRow) return false;
                return true;
            }));
            onChange?.(stillAllowed);
            return stillAllowed;
        });
        prevSecurityLevelRef.current = securityLevel;
    }, [securityLevel]);

    useEffect(() => {
        if (!isOpen || !availableItems.length || !securityLevel) return;
        let allowed = [];
        if (isEdit === true && !isSecurityLevelChanged) {
            allowed = template?.rDocsLevel === 1 ? LEVEL1_ALLOWED : LEVEL2_ALLOWED;
        } else {
            allowed = securityLevel === 1 ? LEVEL1_ALLOWED : LEVEL2_ALLOWED;
        }
        const selected = [];
        const available = [];
        const raw = template?.regions || template?.bannedRegions;
        const BannedRegions = Array.isArray(raw)
            ? raw.map(Number).filter(n => !isNaN(n))
            : typeof raw === "string" && raw.trim()
                ? raw.split(",").map(s => Number(s.trim())).filter(n => !isNaN(n))
                : [];
        const Ips = template?.ips || template?.allowedIP;
        const AllowedIP = Array.isArray(Ips)
            ? Ips.filter(Boolean).join(",")
            : (Ips || "");
        const RestrictToDomains =
            template?.domains || template?.restricttoDomains || template?.restrictToDomains || "";
        if (initialDomainsRef.current === null) {
            initialDomainsRef.current = isEdit && template
                ? RestrictToDomains
                : (Domains || "");
        }

        const RestrictionValues = {
            [restrictToDomainLabel]: RestrictToDomains,
            [allowedIPLabel]: AllowedIP,
            [bannedRegionsLabel]: BannedRegions,
        };

        availableItems.forEach(item => {
            const label = item.label || item;

            if (MAX_VALUE_KEYS.includes(label)) {
                const templateKeyMap = {
                    [selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader]: "maxReadsPerReader",
                    [selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument]: "maxViewsPerDocument",
                };
                const tKey = templateKeyMap[label];
                const templateVal = template?.[tKey];
                const isL2L3Only = L2_L3_ONLY_MAX.includes(label);
                const effectiveLevel = (isEdit && !isSecurityLevelChanged)
                    ? (template?.rDocsLevel ?? securityLevel)
                    : securityLevel;
                const allowedBySecurity = isL2L3Only ? effectiveLevel >= 2 : true;
                const wasExplicitlySelected = isEdit
                    && Number(templateVal) > 0
                    && allowedBySecurity
                    && !userRemovedMaxItems.current.has(label);

                if (wasExplicitlySelected) {
                    setLocalMaxValues(prev => ({ ...prev, [label]: templateVal }));
                    selected.push(item);
                } else {
                    available.push(item);
                }
                return;
            }
            if (DYNAMIC_RESTRICTION_KEYS.includes(label)) {
                const templateVal = RestrictionValues[label];
                const isL2L3Only = DYNAMIC_L2L3_ONLY_RESTRICTIONS.includes(label);
                const effectiveLevel = (isEdit && !isSecurityLevelChanged)
                    ? (template?.rDocsLevel ?? securityLevel)
                    : securityLevel;
                const allowedBySecurity = isL2L3Only ? effectiveLevel >= 2 : true;

                const hasTemplateValue = Array.isArray(templateVal)
                    ? templateVal.length > 0
                    : !!(templateVal && String(templateVal).trim().length > 0);

                if (isEdit && hasTemplateValue && allowedBySecurity && !userRemovedRestrictionItems.current.has(label)) {
                    selected.push(item);
                } else {
                    available.push(item);
                }
                return;
            }
            const templateKey = Object.keys(TEMPLATE_FEATURE_MAP || {}).find(
                key => TEMPLATE_FEATURE_MAP[key] === label
            );
            const isEnabledInTemplate =
                templateKey && template?.[templateKey] === true;
            const isAllowedBySecurity = allowed.includes(label);
            if (isAllowedBySecurity && isEnabledInTemplate) {
                selected.push(item);
            } else {
                available.push(item);
            }
        });
        setRightList(selected);
        setLeftList(available);
        onChange?.(selected);

        const settingsDefaults = {
            [restrictToDomainLabel]: Domains || "",
            [allowedIPLabel]: Array.isArray(ConstMultipleIps)
                ? ConstMultipleIps.filter(Boolean).join(",")
                : (ConstMultipleIps || ""),
            [bannedRegionsLabel]: Array.isArray(ConstContinents)
                ? ConstContinents.map(Number).filter(n => !isNaN(n))
                : (typeof ConstContinents === "string" && ConstContinents.trim()
                    ? ConstContinents.split(",").map(s => Number(s.trim())).filter(n => !isNaN(n))
                    : []),
        };

        const merged = {};
        for (const key of DYNAMIC_RESTRICTION_KEYS) {
            const templateVal = RestrictionValues[key];
            const hasTemplateValue = Array.isArray(templateVal)
                ? templateVal.length > 0
                : !!(templateVal && String(templateVal).trim().length > 0);
            if (userEditedRestrictionItems.current.has(key)) {
                merged[key] = localRestrictionValues[key];
            } else if (hasTemplateValue) {
                merged[key] = templateVal;
            } else {
                merged[key] = settingsDefaults[key];
            }
        }
        setLocalRestrictionValues(merged);
        onRestrictionValueChange?.({
            restrictToDomains: merged[restrictToDomainLabel],
            allowedIP: merged[allowedIPLabel],
            bannedRegions: merged[bannedRegionsLabel],
        });

        if (isEdit && template) {
            const maxPrintsPerReader = template.maxPrintsPerReader > 0 ? String(template.maxPrintsPerReader) : "";
            const maxPrintsPerDocument = template.maxPrintsPerDocument > 0 ? String(template.maxPrintsPerDocument) : "";
            setPrintPopup(prev => ({
                ...prev,
                perReaderVal: maxPrintsPerReader,
                perDocumentVal: maxPrintsPerDocument,
            }));
            const isMaxReadsInSelected = selected.some(i => (i.label || i) === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader);
            const isMaxViewsInSelected = selected.some(i => (i.label || i) === selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument);
            onMaxValueChange?.({
                maxReadsPerReader: isMaxReadsInSelected ? (template.maxReadsPerReader || 0) : (maxReadsText || 0),
                maxViewsPerDocument: isMaxViewsInSelected ? (template.maxViewsPerDocument || 0) : (maxViewsPerDocumentText || 0),
                maxPrintsPerReader: parseInt(maxPrintsPerReader, 10) || 0,
                maxPrintsPerDocument: parseInt(maxPrintsPerDocument, 10) || 0,
            });
        }
    }, [template, availableItems, isSecurityLevelChanged, isOpen, restrictToDomainLabel, allowedIPLabel, bannedRegionsLabel]);

    const didMountSync = useRef(false);
    useEffect(() => {
        if (didMountSync.current) return;
        if (rightList && rightList.length > 0) {
            didMountSync.current = true;
            onChange?.(rightList);
        }
    }, [rightList]);

    const moveRight = () => {
        let updatedMaxValues = { ...localMaxValues };
        let maxValuesChanged = false;

        for (const item of selectedLeft) {
            const label = item.label || item;
            if (MAX_VALUE_KEYS.includes(label)) {
                const val = localMaxValues[label] || accountMaxValues[label] || 0;
                if (!val || val <= 0) {
                    const msg = label === MAX_VALUE_KEYS[0] ? selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValueForMaxReadsPerReader : selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValueForMaxViewsPerDocument;
                    onAlert?.(msg);
                    return;
                }
                if (accountMaxValues[label] && (!localMaxValues[label] || localMaxValues[label] === 0)) {
                    updatedMaxValues = { ...updatedMaxValues, [label]: accountMaxValues[label] };
                    maxValuesChanged = true;
                } else if (localMaxValues[label] > 0) {
                    maxValuesChanged = true;
                }
            }
            if (DYNAMIC_RESTRICTION_KEYS.includes(label)) {
                const val = localRestrictionValues[label];
                const isEmpty = Array.isArray(val)
                    ? val.length === 0
                    : !val || val.trim() === "";
                if (label === bannedRegionsLabel && isEmpty) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastoneBannedRegionisrequiredtoselectthisfeature);
                    return;

                }
                if (label === restrictToDomainLabel && isEmpty) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_Atleastonedomainisrequiredtoselectthisfeature);
                    return;

                }
                if (label === allowedIPLabel && isEmpty) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastoneIPisrequiredtoselectthisfeature);
                    return;

                }

            }
        }
        if (maxValuesChanged) {
            setLocalMaxValues(updatedMaxValues);
            notifyMaxValueChange(updatedMaxValues, printPopup.perReaderVal, printPopup.perDocumentVal);
        }
        const newRight = [...rightList, ...selectedLeft];
        const newLeft = leftList.filter(item => !selectedLeft.includes(item));
        setRightList(newRight);
        setLeftList(newLeft);
        setSelectedLeft([]);
        onChange?.(newRight);
    };
    const moveLeft = () => {
        const newLeft = [...leftList, ...selectedRight];
        const newRight = rightList.filter(item => !selectedRight.includes(item));
        setLeftList(newLeft);
        setRightList(newRight);
        setSelectedRight([]);
        const movedBackMaxKeys = selectedRight
            .map(item => item.label || item)
            .filter(label => MAX_VALUE_KEYS.includes(label));
        if (movedBackMaxKeys.length > 0) {
            movedBackMaxKeys.forEach(key => userRemovedMaxItems.current.add(key));
            const updated = { ...localMaxValues };
            //movedBackMaxKeys.forEach(key => { updated[key] = 0; });
            setLocalMaxValues(updated);
            notifyMaxValueChange(updated, printPopup.perReaderVal, printPopup.perDocumentVal);
        }
        const movedBackRestrictionKeys = selectedRight
            .map(item => item.label || item)
            .filter(label => DYNAMIC_RESTRICTION_KEYS.includes(label));
        if (movedBackRestrictionKeys.length > 0) {
            const updated = { ...localRestrictionValues };

            const configuredValues = {
                [restrictToDomainLabel]: Domains || "",
                [allowedIPLabel]: Array.isArray(ConstMultipleIps)
                    ? ConstMultipleIps.filter(Boolean).join(",")
                    : (ConstMultipleIps || ""),
                [bannedRegionsLabel]: Array.isArray(ConstContinents)
                    ? ConstContinents.map(Number).filter(n => !isNaN(n))
                    : (typeof ConstContinents === "string" && ConstContinents.trim()
                        ? ConstContinents.split(",").map(s => Number(s.trim())).filter(n => !isNaN(n))
                        : []),
            };

            movedBackRestrictionKeys.forEach(label => {
                userRemovedRestrictionItems.current.add(label);
                // userEditedRestrictionItems.current.delete(label);

                // const configuredVal = configuredValues[label];
                // const hasConfiguredValue = Array.isArray(configuredVal)
                //     ? configuredVal.length > 0
                //     : !!(configuredVal && String(configuredVal).trim().length > 0);

                // updated[label] = hasConfiguredValue
                //     ? configuredVal
                //     : (label === bannedRegionsLabel ? [] : "");
            });
            setLocalRestrictionValues(updated);
            onRestrictionValueChange?.({
                restrictToDomains: updated[restrictToDomainLabel],
                allowedIP: updated[allowedIPLabel],
                bannedRegions: updated[bannedRegionsLabel],
            });
        }
        onChange?.(newRight);
    };
    const notifyMaxValueChange = (updatedValues, perReaderVal, perDocumentVal) => {
        const pReader = perReaderVal !== undefined ? perReaderVal : printPopup.perReaderVal;
        const pDoc = perDocumentVal !== undefined ? perDocumentVal : printPopup.perDocumentVal;
        onMaxValueChange?.({
            maxReadsPerReader: updatedValues[selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader] || 0,
            maxViewsPerDocument: updatedValues[selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument] || 0,
            maxPrintsPerReader: parseInt(pReader, 10) || 0,
            maxPrintsPerDocument: parseInt(pDoc, 10) || 0,
        });
    };
    const handleEditPopupSave = () => {
        const parsed = parseInt(editPopup.inputVal, 10);
        if (!editPopup.inputVal || editPopup.inputVal.trim() === "" || isNaN(parsed) || parsed <= 0) {
            onAlert?.(
                editPopup.key === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader
                    ? selectedLanguage.LstLanguageCodes.UI_MaxReadsperReaderCannotBeEmpty
                    : selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocumentCannotBeEmpty
            );
            return;
        }
        if (editPopup.key === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader && !isNaN(parsed) && parsed > 100000) {

            return;
        }
        if (editPopup.key === selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument && !isNaN(parsed) && parsed > 100000) {
            onAlert?.(selectedLanguage.LstLanguageCodes.UI_TheMaximumLimitForMaxViewsPerDoc);
            return;
        }
        const val = Math.max(1, parsed);
        const updated = { ...localMaxValues, [editPopup.key]: val };
        const reads = selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader;
        const views = selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument;
        const maxreadsperreader = editPopup.key === reads ? val : (parseInt(localMaxValues[reads], 10) || 0);
        const maxviewsperdocument = editPopup.key === views ? val : (parseInt(localMaxValues[views], 10) || 0);

        const bothInLeftList = leftList.some(i => (i.label || i) === reads) && leftList.some(i => (i.label || i) === views);
        const bothInRightList = rightList.some(i => (i.label || i) === reads) && rightList.some(i => (i.label || i) === views);

        if ((bothInLeftList || bothInRightList) && maxreadsperreader > 0 && maxviewsperdocument > 0 && maxreadsperreader > maxviewsperdocument) {
            onAlert?.(selectedLanguage.LstLanguageCodes.UI_Readsperreadercannotexceedviewsperdocument);
            return;
        }
        setLocalMaxValues(updated);
        notifyMaxValueChange(updated, printPopup.perReaderVal, printPopup.perDocumentVal);
        setEditPopup({ visible: false, key: null, inputVal: "" });
        setIsSaved(true);
    };

    const openEditPopup = (label, e) => {
        e.stopPropagation();
        const accountDefaults = {
            [selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader]: maxReadsText || 0,
            [selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument]: maxViewsPerDocumentText || 0,
        };
        const storedVal = localMaxValues[label];
        const displayVal = storedVal > 0 ? String(storedVal)
            : (accountDefaults[label] > 0 ? String(accountDefaults[label]) : "");
        if (label === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader && securityLevel === 1) {
            setEditPopup({
                visible: false, key: label, inputVal: displayVal
            });
            return;
        }
        setEditPopup({ visible: true, key: label, inputVal: displayVal });
    };
    const openPrintPopup = (e) => {
        e.stopPropagation();
        const preReader = userEditedPrintValues.current.perReaderVal
            ? printPopup.perReaderVal
            : (printPopup.perReaderVal ||
                (maxPrintsPerReaderText > 0 ? String(maxPrintsPerReaderText) : ""));
        const preDoc = userEditedPrintValues.current.perDocumentVal
            ? printPopup.perDocumentVal
            : (printPopup.perDocumentVal ||
                (maxPrintsPerDocumentText > 0 ? String(maxPrintsPerDocumentText) : ""));
        pendingPrintBackup.current = { perReaderVal: preReader, perDocumentVal: preDoc };
        setPrintPopup(prev => ({ ...prev, visible: true, perReaderVal: preReader, perDocumentVal: preDoc }));
    };


    const handlePrintPopupCancel = () => {
        if (pendingPrintBackup.current !== null) {
            const restoredReader = pendingPrintBackup.current.perReaderVal;
            const restoredDoc = pendingPrintBackup.current.perDocumentVal;
            pendingPrintBackup.current = null;
            setPrintPopup(prev => ({
                ...prev,
                visible: false,
                perReaderVal: restoredReader,
                perDocumentVal: restoredDoc,
            }));
        } else {
            setPrintPopup(prev => ({ ...prev, visible: false }));
        }
    };
    const handlePrintPopupSave = () => {
        const readerVal = securityLevel === 1 ? "" : printPopup.perReaderVal;
        const docVal = printPopup.perDocumentVal;
        const MaxPrintsPerReader = parseInt(readerVal, 10) || 0;
        const MaxPrintsPerDocument = parseInt(docVal, 10) || 0;
        if (MaxPrintsPerDocument > 0 && MaxPrintsPerReader > MaxPrintsPerDocument) {
            onAlert?.(selectedLanguage.LstLanguageCodes.UI_Printsperreadercannotexceedprintspersdocument);
            return;
        }
        pendingPrintBackup.current = null;
        userEditedPrintValues.current = { perReaderVal: true, perDocumentVal: true };
        setPrintPopup(prev => ({ ...prev, visible: false, perReaderVal: readerVal, perDocumentVal: docVal }));
        setIsSaved(true);
        notifyMaxValueChange(localMaxValues, readerVal, docVal);
    };
    const openRestrictionPopup = (label, e) => {
        e.stopPropagation();
        if (label === restrictToDomainLabel && securityLevel === 1) {
            setRestrictionPopup({ visible: false, key: label, pendingMoveItem: null });
            return;
        }
        pendingRestrictionBackup.current = { ...localRestrictionValues };
        setRestrictionPopup({ visible: true, key: label, pendingMoveItem: null });
    };

    const handleRestrictionPopupSave = () => {
        const key = restrictionPopup.key;

        if (key === restrictToDomainLabel) {
            const domainStr = localRestrictionValues[restrictToDomainLabel] || "";
            if (domainStr === "" && domainStr.length === 0) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_Atleastonedomainisrequiredtoselectthisfeature);
                return;
            }
            if (domainStr !== "" && domainStr.split(",").length === 1 && domainStr.length > 190) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_ThedomainmustbelessthanNum);
                return;
            }
            if (domainStr !== "" && domainStr.split(",").length > 1) {
                let domainvalidation = false;
                domainStr.split(",").forEach((element) => {
                    if (element.length > 190 && !domainvalidation) domainvalidation = true;
                });
                if (domainvalidation) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_ThedomainmustbelessthanNum);
                    return;
                }
            }
            if (domainStr !== "" && domainStr.split(",").length > 1) {
                if (domainStr.split(",").length > 10) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_Addupto10domainsseparatedbycomma);
                    return;
                }
            }
            const domainList = domainStr !== "" ? domainStr.split(",").map(d => d.trim()) : [];
            if (domainList.length >= 1) {
                const uniqueDomains = new Set();
                for (let domain of domainList) {
                    if (uniqueDomains.has(domain.toLowerCase())) {
                        onAlert?.(selectedLanguage.LstLanguageCodes.UI_DuplicateDomain);
                        return;
                    }
                    uniqueDomains.add(domain.toLowerCase());
                    const regex = /^[A-Za-z0-9-]+(\.[A-Za-z]{2,})+$/;
                    if (!regex.test(domain)) {
                        onAlert?.(selectedLanguage.LstLanguageCodes.UI_InvalidDomain);
                        return;
                    }
                }
            }
        }

        if (key === bannedRegionsLabel) {
            const regionVal = localRestrictionValues[bannedRegionsLabel];
            if (!regionVal || (Array.isArray(regionVal) && regionVal.length === 6)) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastOneRegionShouldbePermitted);
                return;
            }
            if (!regionVal || (Array.isArray(regionVal) && regionVal.length === 0)) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastoneBannedRegionisrequiredtoselectthisfeature);
                return;
            }
        }

        if (key === allowedIPLabel) {
            const ipVal = localRestrictionValues[allowedIPLabel] || "";
            const ipList = ipVal.split(",").map(ip => ip.trim()).filter(Boolean);
            if (ipList.length === 0) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastoneIPisrequiredtoselectthisfeature);
                return;
            }
        }
        userEditedRestrictionItems.current.add(key);
        if (key === allowedIPLabel) {
            const ipVal = localRestrictionValues[allowedIPLabel] || "";
            const ipList = ipVal.split(",").map(ip => ip.trim()).filter(Boolean);
            if (ipList.length === 0) {
                onAlert?.(selectedLanguage.LstLanguageCodes.UI_AtleastoneIPisrequiredtoselectthisfeature);
                return;
            }
            const ipformat = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            const seenIps = new Set();
            for (const ip of ipList) {
                if (!ipformat.test(ip)) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_YouhaveenteredanInvalidIP);
                    return;
                }
                if (seenIps.has(ip)) {
                    onAlert?.(selectedLanguage.LstLanguageCodes.UI_YouhaveEnteredAnAlreadyEnteredIpAdress + "!");
                    return;
                }
                seenIps.add(ip);
            }

        }
        onRestrictionValueChange?.({
            restrictToDomains: localRestrictionValues[restrictToDomainLabel],
            allowedIP: localRestrictionValues[allowedIPLabel],
            bannedRegions: localRestrictionValues[bannedRegionsLabel],
        });
        // userEditedRestrictionItems.current.add(key);
        pendingRestrictionBackup.current = null;
        const pendingItem = restrictionPopup.pendingMoveItem;
        setRestrictionPopup({ visible: false, key: null, pendingMoveItem: null });
        if (pendingItem) {
            setRightList(prev => {
                const newRight = [...prev, pendingItem];
                onChange?.(newRight);
                return newRight;
            });
            setLeftList(prev => prev.filter(i => i !== pendingItem));
            setSelectedLeft(prev => prev.filter(i => i !== pendingItem));
        }
    };
    const handleRestrictionPopupCancel = () => {
        if (pendingRestrictionBackup.current !== null) {
            setLocalRestrictionValues(pendingRestrictionBackup.current);
            pendingRestrictionBackup.current = null;
        }
        restrictionInputRef.current = null;
        setRestrictionPopup({ visible: false, key: null, pendingMoveItem: null });
    };
    const toggleSelect = (item, list, setList) => {
        if (list.includes(item)) {
            setList(list.filter(i => i !== item));
        } else {
            setList([...list, item]);
        }
    };
    return (
        <>
            {editPopup.visible && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.25)", zIndex: 9999,
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                    onClick={() => { editInputRef.current = null; setEditPopup({ visible: false, key: null, inputVal: "" }); }}
                >
                    <div
                        className="whiteBg"
                        style={{
                            borderRadius: 6, padding: 0, height: 230,
                            width: 450, boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
                            border: "1px solid #ddd"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ padding: "14px 18px 10px" }}>
                            <h6 style={{ fontWeight: 700, margin: 0, fontSize: "1rem" }}>
                                {selectedLanguage.LstLanguageCodes.UI_ViewRestrictions}
                            </h6>
                        </div>
                        <hr style={{ margin: 0, borderColor: "#ddd" }} />
                        <div style={{
                            padding: "18px 18px 14px", marginRight: 20, marginTop: 25,
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10
                        }}>
                            <label style={{ margin: 0, fontSize: "0.93rem", color: "#333", whiteSpace: "nowrap" }}>
                                {editPopup.key}
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={editPopup.inputVal}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (editPopup.key === selectedLanguage.LstLanguageCodes.UI_MaxReadsperReader) {
                                        const parsedValue = parseInt(value);
                                        if (value === "" || value === "0") {
                                            setEditPopup(p => ({ ...p, inputVal: "" }));
                                        } else if (parsedValue > 100000) {
                                            return;
                                        } else if (parsedValue >= 1 && parsedValue <= 100000) {
                                            setEditPopup(p => ({ ...p, inputVal: String(parsedValue) }));
                                        } else {
                                            if (!/^[1-9]\d*$/.test(value) && value !== "") return;
                                            setEditPopup(p => ({ ...p, inputVal: value }));
                                        }
                                    }
                                    if (editPopup.key === selectedLanguage.LstLanguageCodes.UI_MaxviewsPerDocument) {
                                        const parsedValue = parseInt(value);
                                        if (value === "" || value === "0") {
                                            setEditPopup(p => ({ ...p, inputVal: "" }));
                                            if (textBoxRef.current) textBoxRef.current.value = "";
                                        } else if (parsedValue > 100000) {
                                            onAlert?.(selectedLanguage.LstLanguageCodes.UI_TheMaximumLimitForMaxViewsPerDoc);
                                            return;
                                        } else if (parsedValue >= 1 && parsedValue <= 100000) {
                                            setEditPopup(p => ({ ...p, inputVal: String(parsedValue) }));
                                            if (textBoxRef.current) textBoxRef.current.value = String(parsedValue);
                                        } else {
                                            if (!/^[1-9]\d*$/.test(value) && value !== "") return;
                                            setEditPopup(p => ({ ...p, inputVal: value }));
                                        }
                                    } else {
                                        if (!/^[1-9]\d*$/.test(value) && value !== "") return;
                                        setEditPopup(p => ({ ...p, inputVal: value }));
                                    }
                                }
                                }
                                placeholder={selectedLanguage.LstLanguageCodes.UI_Unlimited || "Unlimited"}
                                className="whiteBgcolor"
                                style={{
                                    width: 1000, padding: "6px 10px",
                                    borderRadius: 4, border: "1px solid #ccc",
                                    fontSize: "0.93rem", color: "#555"
                                }}
                                ref={el => { if (el && !editInputRef.current) { editInputRef.current = el; el.focus(); } }}
                            />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 18px 18px", marginTop: 26, marginRight: 20, gap: 12 }}>
                            <button
                                className="btn btn-danger px-4"
                                onClick={() => setEditPopup({ visible: false, key: null, inputVal: "" })}
                            >{selectedLanguage.LstLanguageCodes.UI_Cancel}</button>
                            <button
                                className="btn btn-primary px-4"
                                onClick={handleEditPopupSave}
                            >{selectedLanguage.LstLanguageCodes.UI_Save}</button>
                        </div>
                    </div>
                </div>
            )}
            {printPopup.visible && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.25)", zIndex: 9999,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "0 16px"
                    }}
                    onClick={handlePrintPopupCancel}
                >
                    <div
                        className="whiteBgcolor"
                        style={{
                            borderRadius: 6,
                            padding: 0,
                            width: "fit-content",
                            minWidth: 420,
                            maxWidth: 700,
                            boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
                            border: "1px solid #ddd"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ padding: "14px 18px 10px" }}>
                            <h6 style={{ fontWeight: 700, margin: 0, fontSize: "1rem" }}>
                                {selectedLanguage.LstLanguageCodes.UI_PrintRestrictions}
                            </h6>
                        </div>
                        <hr style={{ margin: 0, borderColor: "#ddd" }} />
                        <div style={{
                            padding: "18px 24px 10px",
                            display: "grid",
                            gridTemplateColumns: "auto 140px",
                            alignItems: "center",
                            gap: "14px 12px"
                        }}>
                            <label style={{
                                margin: 0, fontSize: "0.93rem", color: "#333", whiteSpace: "nowrap"
                            }}>
                                {selectedLanguage.LstLanguageCodes.UI_MaxPrintsPerReader}
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={securityLevel === 1 ? "" : printPopup.perReaderVal}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length > 4) return;
                                    if (!/^[1-9]\d*$/.test(value) && value !== "") return;
                                    userEditedPrintValues.current.perReaderVal = true;
                                    setPrintPopup(p => ({ ...p, perReaderVal: value }));
                                }}
                                disabled={securityLevel === 1}
                                placeholder={selectedLanguage.LstLanguageCodes.UI_Unlimited || "Unlimited"}
                                style={{
                                    width: "100%",
                                    padding: "6px 10px",
                                    borderRadius: 4,
                                    border: "1px solid #ccc",
                                    fontSize: "0.93rem",
                                    color: "#555",
                                    boxSizing: "border-box",
                                    backgroundColor: securityLevel === 1 ? "#f7f7f7" : "#ffffff",
                                    cursor: securityLevel === 1 ? "not-allowed" : "auto"
                                }}
                            />
                            <label style={{ margin: 0, fontSize: "0.93rem", color: "#333", whiteSpace: "nowrap" }}>
                                {selectedLanguage.LstLanguageCodes.UI_MaxPrintsPerDocument}
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={printPopup.perDocumentVal}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length > 4) return;
                                    if (!/^[1-9]\d*$/.test(value) && value !== "") return;
                                    userEditedPrintValues.current.perDocumentVal = true;
                                    setPrintPopup(p => ({ ...p, perDocumentVal: value }));
                                }}
                                placeholder={selectedLanguage.LstLanguageCodes.UI_Unlimited || "Unlimited"}
                                className="whiteBgcolor"
                                style={{
                                    width: "100%",
                                    padding: "6px 10px",
                                    borderRadius: 4,
                                    border: "1px solid #ccc",
                                    fontSize: "0.93rem",
                                    color: "#555",
                                    boxSizing: "border-box"
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 24px 18px", gap: 12 }}>
                            <button
                                className="btn btn-danger px-4"
                                onClick={handlePrintPopupCancel}
                            >{selectedLanguage.LstLanguageCodes.UI_Cancel}</button>
                            <button
                                className="btn btn-primary px-4"
                                style={{
                                    backgroundColor: isSaved ? "#d0ebff" : "#fafafa",
                                    transition: "background-color 0.3s ease"
                                }}
                                onClick={handlePrintPopupSave}
                            >{selectedLanguage.LstLanguageCodes.UI_Save}</button>
                        </div>
                    </div>
                </div>
            )}
            {restrictionPopup.visible && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.25)", zIndex: 9999,
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                    onClick={() => { handleRestrictionPopupCancel(); }}
                >
                    <div
                        className="whiteBgcolor"
                        style={{
                            borderRadius: 6, padding: 0,
                            width: 520, boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
                            border: "1px solid #ddd"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ padding: "14px 18px 10px" }}>
                            <h6 style={{ fontWeight: 700, margin: 0, fontSize: "1rem" }}>
                                {selectedLanguage.LstLanguageCodes.UI_AccessRestrictions}
                            </h6>
                        </div>
                        <hr style={{ margin: 0, borderColor: "#ddd" }} />
                        <div style={{ padding: "18px 24px 10px", display: "flex", flexDirection: "column", gap: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                <label style={{ margin: 0, fontSize: "0.93rem", color: "#333", whiteSpace: "nowrap", minWidth: 150 }}>
                                    {restrictionPopup.key}
                                </label>

                                {restrictionPopup.key === bannedRegionsLabel ? (
                                    <Select
                                        isMulti
                                        name="regions"
                                        options={RegionValues}
                                        value={
                                            Array.isArray(localRestrictionValues[bannedRegionsLabel])
                                                ? localRestrictionValues[bannedRegionsLabel].map(v =>
                                                    RegionValues.find(r => r.value === Number(v))
                                                ).filter(Boolean)
                                                : []
                                        }
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        onChange={(selectedOptions) => {
                                            const values = selectedOptions ? selectedOptions.map(o => o.value) : [];

                                            setLocalRestrictionValues(p => ({
                                                ...p,
                                                [bannedRegionsLabel]: values
                                            }));
                                            userEditedRestrictionItems.current.add(bannedRegionsLabel);
                                        }}

                                        placeholder={selectedLanguage.LstLanguageCodes.UI_Select}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={localRestrictionValues[restrictionPopup.key] || ""}
                                        onChange={(e) => {
                                            const value = restrictionPopup.key === restrictToDomainLabel
                                                ? e.target.value.replace(/\s/g, "")
                                                : e.target.value.replace(/^\s+/, "");

                                            setLocalRestrictionValues(p => ({
                                                ...p,
                                                [restrictionPopup.key]: value
                                            }));
                                            userEditedRestrictionItems.current.add(restrictionPopup.key);
                                        }}
                                        placeholder={
                                            restrictionPopup.key === restrictToDomainLabel
                                                ? selectedLanguage.LstLanguageCodes.UI_ExampleORGExample
                                                : selectedLanguage.LstLanguageCodes.UI_EntereachAuthorizedIP
                                        }
                                        title={
                                            restrictionPopup.key === restrictToDomainLabel
                                                ? selectedLanguage.LstLanguageCodes.UI_ExampleORGExample
                                                : selectedLanguage.LstLanguageCodes.UI_EntereachAuthorizedIP
                                        }
                                        className="whiteBgcolor input-ellipsis"
                                        style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc", fontSize: "0.93rem" }}
                                        ref={el => { if (el && !restrictionInputRef.current) { restrictionInputRef.current = el; el.focus(); } }}
                                    />
                                )}
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 24px 18px", gap: 12 }}>
                            <button className="btn btn-danger px-4"
                                onClick={handleRestrictionPopupCancel}>
                                {selectedLanguage.LstLanguageCodes.UI_Cancel}
                            </button>
                            <button className="btn btn-primary px-4" onClick={handleRestrictionPopupSave}>
                                {selectedLanguage.LstLanguageCodes.UI_Save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="d-flex justify-content-center gap-3">
                <div className="border rounded p-3" style={{ width: "40%", maxHeight: '400px', overflowY: 'auto' }}>
                    <h4 className="fw-bold mb-2" style={{ fontSize: '1.14rem' }}>{selectedLanguage.LstLanguageCodes.UI_AvailableFeatures}</h4>

                    {leftList.map((item, index) => {
                        const label = item.label || item;
                        const isChecked = selectedLeft.includes(item);
                        const isMaxItem = MAX_VALUE_KEYS.includes(label);
                        const isRestrictionRow = DYNAMIC_RESTRICTION_KEYS.includes(label);
                        const isL2L3OnlyMax = L2_L3_ONLY_MAX.includes(label);
                        const isCanPrintRow = label === CAN_PRINT_LABEL;
                        const isLocked =
                            (securityLevel === 1 && !LEVEL1_ALLOWED.includes(label) && !isMaxItem && !isRestrictionRow) ||
                            (isL2L3OnlyMax && securityLevel === 1) ||
                            (DYNAMIC_L2L3_ONLY_RESTRICTIONS.includes(label) && securityLevel === 1);

                        return (
                            <div
                                key={label || index}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: isChecked ? "#d0ebff" : "#fafafa",
                                    padding: "6px 8px",
                                    borderRadius: 4,
                                    border: "1px solid #ddd",
                                    marginBottom: "6px",
                                    cursor: isLocked ? "not-allowed" : "default",
                                    opacity: isLocked ? 0.6 : 1
                                }}
                                onClick={() => {
                                    if (!isLocked) {
                                        toggleSelect(item, selectedLeft, setSelectedLeft);
                                    }
                                }}
                            >
                                <span style={{ flexGrow: 1, display: "flex", alignItems: "center", cursor: isLocked ? "not-allowed" : "default", }}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        readOnly
                                        style={{ marginRight: 8, cursor: "pointer", pointerEvents: "none" }}
                                    />
                                    <span>{label}</span>
                                </span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    {(isMaxItem || isRestrictionRow || isCanPrintRow) && (
                                        <span
                                            className="material-icons"
                                            style={{ fontSize: 16, cursor: isLocked ? "not-allowed" : "pointer" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (isCanPrintRow) openPrintPopup(e);
                                                else if (isRestrictionRow) openRestrictionPopup(label, e);
                                                else openEditPopup(label, e);
                                            }}
                                        >
                                            edit
                                        </span>
                                    )}
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <FeatureTooltip
                                            label={label}
                                            selectedLanguage={selectedLanguage}
                                        />
                                    </span>
                                </span>
                                {item.sortable && (
                                    <span className="material-icons sort-items-popup">sync_alt</span>
                                )}
                            </div>
                        );
                    })}

                    <IdentifyLeakersModal
                        ref={identifyLeakersModalRef}
                        onClose={handleClose}
                        mode="template"
                    />
                </div>

                <div className="d-flex flex-column justify-content-center gap-2">
                    <button
                        className="btn btn-primary px-3"
                        onClick={moveRight}
                        disabled={selectedLeft.length === 0}
                    >
                        &gt;&gt;
                    </button>
                    <button
                        className="btn btn-danger px-3"
                        onClick={moveLeft}
                        disabled={selectedRight.length === 0}
                    >
                        &lt;&lt;
                    </button>
                </div>
                <div className="border rounded p-3" style={{ width: "43%", maxHeight: '400px', overflowY: 'auto' }}>
                    <h4 className="fw-bold mb-2" style={{ fontSize: '1.14rem' }}>{selectedLanguage.LstLanguageCodes.UI_SelectedFeatures}</h4>
                    {rightList.map((item, index) => {
                        const label = item.label || item;
                        const isChecked = selectedRight.includes(item);
                        const isMaxItem = MAX_VALUE_KEYS.includes(label);
                        const isCanPrintRow = label === CAN_PRINT_LABEL;
                        const isRestrictionRow = DYNAMIC_RESTRICTION_KEYS.includes(label);
                        return (
                            <div
                                key={label || index}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: isChecked ? "#d0ebff" : "#fafafa",
                                    padding: "6px 8px",
                                    borderRadius: 4,
                                    border: "1px solid #ddd",
                                    marginBottom: "6px",
                                }}
                                onClick={() =>
                                    toggleSelect(item, selectedRight, setSelectedRight)
                                }
                            >
                                <span style={{ flexGrow: 1, display: "flex", alignItems: "center", cursor: "default", }}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        readOnly
                                        style={{ marginRight: 8, cursor: "pointer", pointerEvents: "none" }}
                                    />
                                    <span>{label}</span>
                                </span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    {(isMaxItem || isCanPrintRow || isRestrictionRow || label == selectedLanguage.LstLanguageCodes.UI_IDLeakers) && (
                                        <span
                                            className="material-icons"
                                            style={{ fontSize: 16, cursor: "pointer" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (label == selectedLanguage.LstLanguageCodes.UI_IDLeakers) {
                                                    handleEditClick();
                                                    return;
                                                }
                                                if (isCanPrintRow) openPrintPopup(e);
                                                else if (isRestrictionRow) openRestrictionPopup(label, e);
                                                else openEditPopup(label, e);
                                            }}
                                        >
                                            edit
                                        </span>
                                    )}
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <FeatureTooltip
                                            label={label}
                                            selectedLanguage={selectedLanguage}
                                        />
                                    </span>
                                </span>
                                {item.sortable && (
                                    <span className="material-icons sort-items-popup">sync_alt</span>
                                )}
                            </div>
                        );
                    })}

                    <IdentifyLeakersModal
                        ref={identifyLeakersModalRef}
                        onClose={handleClose}
                        mode="template"
                    />
                </div>
            </div>
        </>
    );
});

export default DualListSelector;